import React from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  publishPost,
  getAllEductionTopics,
  getPostTagSearch,
  updatePost,
} from "../../api";
import { handleApiError } from "../../utils/apiErrorHandler";
import { getPublisherAvatarUrl } from "../../utils";
import publisherStore from "../../flux/publisherStore";
import { getPublishers } from "../../flux/publisherAction";
import { showLovableToast } from "../../components/common/LovableToast/showLovableToast";
import {
  PostEditorCard,
  PostEditorCardBody,
  PostEditorCardDesc,
  PostEditorCardHead,
  PostEditorCardIcon,
  PostEditorCardTitle,
  PostEditorOutlineBtn,
  PostEditorPreviewBtn,
} from "./PostEditor.styles";

const Icon = ({ children }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);
const DatePickerPortal = ({ children }) =>
  typeof document !== "undefined"
    ? createPortal(children, document.body)
    : children;

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const YEAR_FROM = 2010;
const YEAR_TO = new Date().getFullYear() + 5;
const YEAR_OPTIONS = Array.from(
  { length: YEAR_TO - YEAR_FROM + 1 },
  (_, index) => YEAR_FROM + index
);

const ordinalDay = (day) => {
  const remainder = day % 100;
  if (remainder >= 11 && remainder <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const formatPublishDateLabel = (date) => {
  if (!date) return "";
  const month = date.toLocaleDateString("en-US", { month: "long" });
  return `${month} ${ordinalDay(date.getDate())}, ${date.getFullYear()}`;
};

const PublishDateHeader = ({
  date,
  changeMonth,
  changeYear,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <div className="post-editor-calendar__caption">
    <button
      type="button"
      className="post-editor-calendar__nav post-editor-calendar__nav--prev"
      onClick={decreaseMonth}
      disabled={prevMonthButtonDisabled}
      aria-label="Previous month"
    />
    <div className="post-editor-calendar__dropdowns">
      <select
        className="post-editor-calendar__select"
        value={date.getMonth()}
        onChange={(event) => changeMonth(Number(event.target.value))}
        aria-label="Month"
      >
        {MONTH_OPTIONS.map((month, index) => (
          <option key={month} value={index}>
            {month}
          </option>
        ))}
      </select>
      <select
        className="post-editor-calendar__select"
        value={date.getFullYear()}
        onChange={(event) => changeYear(Number(event.target.value))}
        aria-label="Year"
      >
        {YEAR_OPTIONS.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
    <button
      type="button"
      className="post-editor-calendar__nav post-editor-calendar__nav--next"
      onClick={increaseMonth}
      disabled={nextMonthButtonDisabled}
      aria-label="Next month"
    />
  </div>
);

const EyeIcon = () => (
  <Icon>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
const CalendarIcon = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" x2="16" y1="2" y2="6" />
    <line x1="8" x2="8" y1="2" y2="6" />
    <line x1="3" x2="21" y1="10" y2="10" />
  </Icon>
);
const PublishDateButton = React.forwardRef(
  ({ value, label, onClick, onClear }, ref) => {
    const text = label || value;
    return (
      <div ref={ref} className="post-editor__date-btn" onClick={onClick}>
        <button type="button" className="post-editor__date-main">
          <CalendarIcon />
          {text ? (
            <span>{text}</span>
          ) : (
            <span className="is-placeholder">Select a date</span>
          )}
        </button>
        {text ? (
          <button
            type="button"
            className="post-editor__date-clear"
            aria-label="Clear publish date"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (onClear) onClear();
            }}
          >
            <i className="material-icons">close</i>
          </button>
        ) : null}
      </div>
    );
  }
);
PublishDateButton.displayName = "PublishDateButton";
const LockIcon = () => (
  <Icon>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Icon>
);
const TagsIcon = () => (
  <Icon>
    <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0l8.59-8.59a1 1 0 0 0 0-1.41Z" />
    <circle cx="7.5" cy="7.5" r=".5" />
  </Icon>
);
const PUBLISHER_ROLE_ID = 3;

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const parsePublishedAt = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPublishedAt = (date) => {
  if (!date) return null;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${year}-${month <= 9 ? "0" + month : month}-${
    day <= 9 ? "0" + day : day
  } 12:00:00`;
};

const publisherCannotUnpublish = (user, post) =>
  Boolean(
    user &&
      user.userRoleId === PUBLISHER_ROLE_ID &&
      post &&
      post.id &&
      !post.isDraft
  );

const TableIcon = () => (
  <Icon>
    <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
  </Icon>
);

class PostEditorSidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      profile: "",
      session: "",
      postCreated: false,
      publisherId: null,
      isHidden: false,
      visibility: true,
      isDraft: true,
      slug: "",
      submitDisabled: "",
      tagLine: "",
      searchLine: "",
      tags: [],
      educationalTopics: [],
      educationalTopicsSelected: [],
      suggestedTopicsSearchResults: [],
      tagSuggestions: [],
      showError: false,
      publishers: [],
      publishDate: null,
    };
    this.chosenPublishDate = null;

    this.onPublisherStoreChange = this.onPublisherStoreChange.bind(this);
  }

  onPublisherStoreChange() {
    this.setState({
      publishers: publisherStore.getPublishers() || [],
    });
  }

  componentDidMount = async () => {
    publisherStore.addChangeListener(this.onPublisherStoreChange);

    if (publisherStore.getPublishers().length === 0) {
      getPublishers();
    }

    let topics;
    try {
      topics = await getAllEductionTopics();
    } catch (err) {
      topics = { data: [] };
    }

    this.setState({
      profile: JSON.parse(localStorage.getItem("user")),
      publishers: publisherStore.getPublishers() || [],
      educationalTopics: Array.isArray(topics?.data) ? topics.data : [],
    });
    this.hydrateFromPost(this.props.post);
  };

  componentWillUnmount() {
    publisherStore.removeChangeListener(this.onPublisherStoreChange);
  }

  hydrateFromPost = (post) => {
    if (!post || !post.id || this.hydratedPostId === post.id) return;
    this.hydratedPostId = post.id;
    const savedDate = parsePublishedAt(post.publishedAt);
    const publishDate = this.chosenPublishDate || savedDate || this.state.publishDate || null;
    this.setState({
      isHidden: post.isHidden,
      isDraft: post.isDraft,
      visibility: post.contentAvailabilityId === 2,
      publisherId:
        (post.publisher && post.publisher.id) ||
        (post.author && post.author.id)
          ? String((post.publisher && post.publisher.id) || post.author.id)
          : this.state.publisherId,
      educationalTopicsSelected: Array.isArray(post.educationTopics)
        ? post.educationTopics.map((topic) => topic.id)
        : this.state.educationalTopicsSelected,
      tags: Array.isArray(post.tags) ? post.tags : this.state.tags,
      publishDate,
    });
  };

  componentDidUpdate = (prevProps) => {
    const { post: nextPost } = this.props;
    const { post: prevPost } = prevProps;
    if (nextPost && nextPost.id && nextPost.id !== this.hydratedPostId) {
      this.hydrateFromPost(nextPost);
    }
    if (
      nextPost &&
      nextPost.tags &&
      nextPost.tags.length !== this.state.tags.length &&
      this.state.tags.length === 0
    ) {
      this.setState({
        tags: nextPost.tags,
      });
    }

    if (
      this.props &&
      this.props.post &&
      this.props.post.educationTopics &&
      (!prevPost ||
        JSON.stringify(prevPost.educationTopics) !==
          JSON.stringify(this.props.post.educationTopics))
    ) {
      this.setState({
        educationalTopicsSelected: this.props.post.educationTopics.map(
          (t) => t.id
        ),
      });
    }
  };

  publish = async () => {
    const chosenPublishDate = this.chosenPublishDate || this.state.publishDate;
    const isDraft = this.state.isDraft;
    const tags = this.state.tags;
    const topicIds = this.state.educationalTopicsSelected;
    const visibility = this.state.visibility;
    const isHidden = this.state.isHidden;
    const publisherId = this.state.publisherId;
    this.setState({
      submitDisabled: "disabled",
    });
    if (this.props.onBeforeSubmit) {
      await this.props.onBeforeSubmit();
    }
    const image =
      (this.props.getFeaturedImage && this.props.getFeaturedImage()) ||
      this.props.image;
    const data = {
      authorId: publisherId,
      contentAvailabilityId: visibility ? 2 : 1,
      isHidden: isHidden,
      postContentTypeId: this.props.type ? 2 : 1,
      isDraft: isDraft,
      title: this.props.title,
      // Always empty: backend generates the URL-safe slug. Sending a
      // client slug (from the title field) caused public post 404s.
      slug: this.state.slug,
      excerpt:
        (this.props.getExcerpt && this.props.getExcerpt()) ||
        this.props.description,
      isCommunity: false,
      content:
        (this.props.getContent && this.props.getContent()) ||
        this.props.content,
      tags: tags.map((t) => t.name),
      suggestedPostIds: (this.props.suggested || []).map((t) => t.id),
      educationTopicIds: topicIds,
    };
    const publishedAt = formatPublishedAt(chosenPublishDate);
    if (publishedAt) {
      data.publishedAt = publishedAt;
    }
    if (image && String(image).indexOf("http") !== 0) {
      data.image = image;
    }
    if (this.props.type && this.props.video) {
      data.embeddedVideo = this.props.video;
    }

    const res = await publishPost(data);

    if (res && res.status >= 200 && res.status < 300) {
      showLovableToast("Post created successfully!");
      this.setState(
        {
          postCreated: true,
          showError: false,
          submitDisabled: "",
        },
        () => {
          if (this.props.onPublicationSaved) {
            this.props.onPublicationSaved({
              ...(res.data || {}),
              isDraft: data.isDraft,
              publishedAt: (res.data && res.data.publishedAt) || data.publishedAt || null,
            });
          }
          if (this.props.onSidebarUpdate) {
            this.props.onSidebarUpdate(res && res.data);
          }
        }
      );
    } else {
      const errorResponse = handleApiError(
        res,
        "An error occurred while creating the post. Please try again.",
        { notify: false }
      );
      showLovableToast(errorResponse.message);
      this.setState({
        showError: true,
        errorText: errorResponse.message,
        submitDisabled: "",
      });
    }
  };

  setPublishDate = (date) => {
    if (!date) return;
    const picked = date instanceof Date ? new Date(date.getTime()) : new Date(date);
    if (Number.isNaN(picked.getTime())) return;
    const day = new Date(picked.getTime());
    day.setHours(0, 0, 0, 0);
    const user = this.getCurrentUser();
    if (
      user &&
      Number(user.userRoleId) === PUBLISHER_ROLE_ID &&
      day < startOfToday()
    ) {
      return;
    }
    this.chosenPublishDate = picked;
    this.publishDateChosenAt = Date.now();
    this.publishDateTouched = true;
    this.setState({ publishDate: picked });
  };

  clearPublishDate = () => {
    if (Date.now() - (this.publishDateChosenAt || 0) < 500) return;
    this.chosenPublishDate = null;
    this.publishDateTouched = true;
    this.setState({ publishDate: null });
  };

  selectEducational = (event, id) => {
    let temp;
    if (this.state.educationalTopicsSelected.includes(id)) {
      temp = [...this.state.educationalTopicsSelected];
      const index = temp.indexOf(id);
      temp.splice(index, 1);
      this.setState({ educationalTopicsSelected: temp });
    } else {
      temp = [...this.state.educationalTopicsSelected];
      temp.push(id);
      this.setState({ educationalTopicsSelected: temp });
    }

    if (this.props.onSelectEducationalTopic) {
      this.props.onSelectEducationalTopic(
        this.state.educationalTopics.filter((educationalTopic) =>
          temp.includes(educationalTopic.id)
        ),
        this.state
      );
    }
  };

  update = async () => {
    const chosenPublishDate = this.chosenPublishDate || this.state.publishDate;
    const tags = this.state.tags;
    const topicIds = this.state.educationalTopicsSelected;
    const visibility = this.state.visibility;
    const isHidden = this.state.isHidden;
    const isDraft = publisherCannotUnpublish(
      this.getCurrentUser(),
      this.props.savedPublication
    )
      ? false
      : this.state.isDraft;
    if (!this.props.post || !this.props.post.id) {
      return;
    }
    if (this.props.onBeforeSubmit) {
      await this.props.onBeforeSubmit();
    }
    const image =
      (this.props.getFeaturedImage && this.props.getFeaturedImage()) ||
      this.props.image;
    const data = {
      title: this.props.title,
      excerpt:
        (this.props.getExcerpt && this.props.getExcerpt()) ||
        this.props.description,
      content:
        (this.props.getContent && this.props.getContent()) ||
        this.props.content,
      tags: tags.map((t) => t.name),
      isDraft: isDraft,
      suggestedPostIds: (this.props.suggested || []).map((t) => t.id),
      educationTopicIds: topicIds,
    };
    if (isHidden !== null) {
      data["isHidden"] = isHidden;
    }
    if (visibility !== null) {
      data["contentAvailabilityId"] = visibility ? 2 : 1;
    }
    if (image && String(image).indexOf("http") !== 0) {
      data.image = image;
    }
    if (this.props.type && this.props.video) {
      data.embeddedVideo = this.props.video;
    }
    const publishedAt = formatPublishedAt(
      this.publishDateTouched ? chosenPublishDate : null
    );
    if (publishedAt) {
      data.publishedAt = publishedAt;
    }

    const res = await updatePost(this.props.post.id, data);

    if (res && res.status >= 200 && res.status < 300) {
      showLovableToast("Post updated successfully!");
      this.setState(
        {
          postUpdated: true,
          showError: false,
        },
        () => {
          this.props.reloadContent({
            ...(res.data || {}),
            id: (res.data && res.data.id) || this.props.post.id,
            isDraft: data.isDraft,
            publishedAt:
              data.publishedAt ||
              (res.data && res.data.publishedAt) ||
              (this.props.savedPublication &&
                this.props.savedPublication.publishedAt) ||
              null,
          });
        }
      );
    } else {
      const errorResponse = handleApiError(
        res,
        "An error occurred while updating the post. Please try again.",
        { notify: false }
      );
      showLovableToast(errorResponse.message);
      this.setState({
        showError: true,
        errorText: errorResponse.message,
      });
    }
  };

  visibility = () => {
    const visibility = !this.state.visibility;
    this.setState({ visibility }, () => {
      this.props.onSidebarUpdate({
        ...this.state,
        contentAvailabilityId: this.state.visibility ? 2 : 1,
      });
    });
  };

  isHidden = () => {
    const isHidden = !this.state.isHidden;
    this.setState({ isHidden }, () => {
      this.props.onSidebarUpdate({ ...this.state, isHidden });
    });
  };

  changePublisher = (event) => {
    const rawValue = event && event.target && event.target.value;
    const publisherId = rawValue === "" ? null : rawValue;
    this.setState({ publisherId }, () => {
      if (this.props.onChangePublisher) {
        this.props.onChangePublisher(publisherId);
      }

      if (this.props.onSidebarUpdate) {
        this.props.onSidebarUpdate(this.state);
      }
    });
  };

  setTags = async (event) => {
    const tag = event.target.value;
    this.setState({
      tagLine: tag,
    });
    let res = await getPostTagSearch(tag);
    if (res !== undefined) {
      this.setState({ tagSuggestions: res.data });
    }
  };

  clearTags = () => {
    this.setState({
      tagLine: "",
      tagSuggestions: [],
    });
  };

  addTag = (tag) => {
    this.setState({ tags: [...this.state.tags, tag] }, () => {
      this.props.onTagsUpdate(this.state.tags);
    });
    this.clearTags();
  };

  tagKeyPress = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      let val = e.target.value;
      this.addTag({
        name: val,
      });
    }
  };

  removeTag = (tag) => {
    let tags = this.state.tags;
    tags = tags.filter((t) => t.name !== tag.name);
    this.setState({ tags: tags }, () => {
      if (this.props.onTagsUpdate) {
        this.props.onTagsUpdate(this.state.tags);
      }
    });
  };

  changeStatus = (event) => {
    const isDraft = event.target.value === "true";
    const user = this.getCurrentUser();
    if (isDraft && publisherCannotUnpublish(user, this.props.savedPublication)) return;
    this.setState({ isDraft }, () => {
      if (this.props.onSidebarUpdate) {
        this.props.onSidebarUpdate({ ...this.state, isDraft });
      }
    });
  };

  submitCurrent = () => {
    const postId = this.props.post && this.props.post.id;
    if (postId) {
      this.update();
      return;
    }
    this.publish();
  };

  getCurrentUser() {
    try {
      const user = localStorage.getItem("user");
      return user && JSON.parse(user);
    } catch (err) {
      return null;
    }
  }

  render() {
    const user = this.getCurrentUser();
    const postId = this.props.post && this.props.post.id;
    const cannotUnpublish = publisherCannotUnpublish(
      user,
      this.props.savedPublication
    );
    const isPublisher = user && user.userRoleId === PUBLISHER_ROLE_ID;
    const publishers = this.state.publishers || [];
    const selectedPublisher =
      publishers.find(
        (publisher) => String(publisher.id) === String(this.state.publisherId)
      ) || null;

    return (
      <>
        <PostEditorCard>
          <PostEditorCardHead>
            <PostEditorCardIcon>
              <EyeIcon />
            </PostEditorCardIcon>
            <div>
              <PostEditorCardTitle>Publish</PostEditorCardTitle>
            </div>
          </PostEditorCardHead>
          <PostEditorCardBody>
            <fieldset className="post-editor__lock-fields post-editor__lock-fields--inline">
            <div className="post-editor__field">
              <span className="post-editor__label">Status</span>
              <div className="post-editor__seg" role="group" aria-label="Status">
                <button
                  type="button"
                  className={`post-editor__seg-btn${
                    this.state.isDraft ? " is-active" : ""
                  }`}
                  disabled={cannotUnpublish}
                  title={
                    cannotUnpublish
                      ? "Published posts stay published."
                      : undefined
                  }
                  onClick={() =>
                    this.changeStatus({ target: { value: "true" } })
                  }
                >
                  Draft
                </button>
                <button
                  type="button"
                  className={`post-editor__seg-btn${
                    !this.state.isDraft ? " is-active" : ""
                  }`}
                  onClick={() =>
                    this.changeStatus({ target: { value: "false" } })
                  }
                >
                  Published
                </button>
              </div>
              {cannotUnpublish && (
                <p className="post-editor__field-note">
                  Published posts stay published.
                </p>
              )}
            </div>

            <div className="post-editor__field">
              <span className="post-editor__label">Publish date</span>
              <div className="post-editor__datepicker">
                <DatePicker
                  selected={this.state.publishDate || null}
                  dateFormat="MMMM do, yyyy"
                  onChange={this.setPublishDate}
                  onSelect={this.setPublishDate}
                  minDate={isPublisher ? startOfToday() : undefined}
                  shouldCloseOnSelect
                  showPopperArrow={false}
                  popperPlacement="bottom-start"
                  popperClassName="post-editor-datepicker-popper"
                  calendarClassName="post-editor-calendar"
                  popperContainer={DatePickerPortal}
                  renderCustomHeader={PublishDateHeader}
                  customInput={
                    <PublishDateButton
                      label={formatPublishDateLabel(this.state.publishDate)}
                      onClear={this.clearPublishDate}
                    />
                  }
                />
              </div>
            </div>
            </fieldset>

            <div className="post-editor__actions-row">
              <PostEditorOutlineBtn
                type="button"
                onClick={this.props.onPreview}
              >
                <EyeIcon />
                Preview
              </PostEditorOutlineBtn>
              {!postId ? (
                <PostEditorPreviewBtn
                  type="button"
                  disabled={!!this.state.submitDisabled}
                  onClick={this.publish}
                >
                  Submit
                </PostEditorPreviewBtn>
              ) : (
                <PostEditorPreviewBtn
                  type="button"
                  onClick={this.update}
                >
                  Update
                </PostEditorPreviewBtn>
              )}
            </div>

            {this.state.postCreated && (
              <div className="post-editor__alert post-editor__alert--ok">
                Post created!
              </div>
            )}
            {this.state.postUpdated && (
              <div className="post-editor__alert post-editor__alert--ok">
                Post updated!
              </div>
            )}
            {this.state.showError && (
              <div className="post-editor__alert post-editor__alert--err">
                {this.state.errorText}
              </div>
            )}
          </PostEditorCardBody>
        </PostEditorCard>

        {user && user.userRoleId === 1 && (
          <PostEditorCard>
            <PostEditorCardHead>
              <PostEditorCardIcon>
                <LockIcon />
              </PostEditorCardIcon>
              <div>
                <PostEditorCardTitle>Admin options</PostEditorCardTitle>
              </div>
            </PostEditorCardHead>
            <PostEditorCardBody>
              <div className="post-editor__field">
                <span className="post-editor__label">Access</span>
                <div
                  className="post-editor__seg"
                  role="group"
                  aria-label="Content availability"
                >
                  <button
                    type="button"
                    className={`post-editor__seg-btn${
                      this.state.visibility ? " is-active" : ""
                    }`}
                    onClick={() => {
                      if (!this.state.visibility) this.visibility();
                    }}
                  >
                    Paid
                  </button>
                  <button
                    type="button"
                    className={`post-editor__seg-btn${
                      !this.state.visibility ? " is-active" : ""
                    }`}
                    onClick={() => {
                      if (this.state.visibility) this.visibility();
                    }}
                  >
                    Free
                  </button>
                </div>
              </div>

              <div className="post-editor__field">
                <span className="post-editor__label">Visibility</span>
                <div
                  className="post-editor__seg"
                  role="group"
                  aria-label="Visibility"
                >
                  <button
                    type="button"
                    className={`post-editor__seg-btn${
                      !this.state.isHidden ? " is-active" : ""
                    }`}
                    onClick={() => {
                      if (this.state.isHidden) this.isHidden();
                    }}
                  >
                    Shown
                  </button>
                  <button
                    type="button"
                    className={`post-editor__seg-btn${
                      this.state.isHidden ? " is-active" : ""
                    }`}
                    onClick={() => {
                      if (!this.state.isHidden) this.isHidden();
                    }}
                  >
                    Hidden
                  </button>
                </div>
              </div>

              <div className="post-editor__field">
                <span className="post-editor__label">Publish on behalf of</span>
                <div className="post-editor__publishers">
                  {publishers.map((publisher) => {
                    const active =
                      String(this.state.publisherId) === String(publisher.id);
                    const avatar = getPublisherAvatarUrl(publisher);
                    return (
                      <button
                        type="button"
                        key={publisher.id}
                        title={publisher.name}
                        aria-label={publisher.name}
                        aria-pressed={active}
                        className={`post-editor__publisher${
                          active ? " is-active" : ""
                        }`}
                        onClick={() =>
                          this.changePublisher({
                            target: { value: String(publisher.id) },
                          })
                        }
                      >
                        {avatar ? (
                          <img src={avatar} alt="" />
                        ) : (
                          <span className="post-editor__publisher-fallback">
                            {(publisher.name || "?").slice(0, 1)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="post-editor__publishing-as">
                  Publishing as{" "}
                  {selectedPublisher ? selectedPublisher.name : "unselected"}
                </div>
              </div>
            </PostEditorCardBody>
          </PostEditorCard>
        )}

        <fieldset className="post-editor__lock-fields post-editor__lock-fields--card">
        <PostEditorCard>
          <PostEditorCardHead>
            <PostEditorCardIcon>
              <TagsIcon />
            </PostEditorCardIcon>
            <div>
              <PostEditorCardTitle>Tags</PostEditorCardTitle>
              <PostEditorCardDesc>
                Search, add, or remove tags.
              </PostEditorCardDesc>
            </div>
          </PostEditorCardHead>
          <PostEditorCardBody>
            {this.state.tags.length > 0 && (
              <div className="post-editor__tags">
                {this.state.tags.map((tag) => (
                  <span key={tag.name} className="post-editor__tag">
                    {tag.name}
                    <button
                      type="button"
                      aria-label={`Remove ${tag.name}`}
                      onClick={() => this.removeTag(tag)}
                    >
                      <i className="material-icons">close</i>
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="post-editor__search-wrap">
              <i className="material-icons" aria-hidden="true">
                search
              </i>
              <input
                className="post-editor__input"
                placeholder="Search or add tag"
                onKeyDown={this.tagKeyPress}
                onChange={this.setTags}
                value={this.state.tagLine}
              />
            </div>
            {this.state.tagSuggestions.length > 0 && (
              <ul className="post-editor__suggestions">
                {this.state.tagSuggestions.map((tag) => (
                  <li key={tag.id || tag.name}>
                    <button
                      type="button"
                      className="post-editor__suggestion"
                      onClick={() => this.addTag(tag)}
                    >
                      {tag.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </PostEditorCardBody>
        </PostEditorCard>

        <PostEditorCard>
          <PostEditorCardHead>
            <PostEditorCardIcon>
              <TableIcon />
            </PostEditorCardIcon>
            <div>
              <PostEditorCardTitle>Education topics</PostEditorCardTitle>
              <PostEditorCardDesc>
                Choose every topic this post supports.
              </PostEditorCardDesc>
            </div>
          </PostEditorCardHead>
          <PostEditorCardBody>
            <fieldset className="post-editor__topics">
              {this.state.educationalTopics.map((topic) => (
                <label key={topic.id} className="post-editor__topic">
                  <input
                    type="checkbox"
                    checked={this.state.educationalTopicsSelected.some(
                      (selectedId) => String(selectedId) === String(topic.id)
                    )}
                    value={topic.id}
                    onChange={(e) => this.selectEducational(e, topic.id)}
                  />
                  <span className="post-editor__topic-box" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <polyline
                        points="20 6 9 17 4 12"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>{topic.name}</span>
                </label>
              ))}
            </fieldset>
          </PostEditorCardBody>
        </PostEditorCard>
        </fieldset>
      </>
    );
  }
}

export default PostEditorSidebar;
